import { Injector } from '@angular/core';
import {
  TaskInstance,
  TaskListState,
  TaskListType,
} from '@data-access/tasks/types';
import { signalStoreFeature, type } from '@ngrx/signals';
import {
  entityConfig,
  setAllEntities,
  updateEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { on, withReducer } from '@ngrx/signals/events';
import { TASK_EVENTS } from '../../signal-events';

const taskEntity = () =>
  entityConfig({
    collection: '_task',
    entity: type<TaskInstance>(),
    selectId: (entity) => entity.id,
  });
const _documentEntityConfig = () =>
  entityConfig({
    collection: '_documents',
    entity: type<TaskInstance>(),
    selectId: (entity) => entity.id,
  });
export const _sectionEntityConfig = () =>
  entityConfig({
    collection: '_sections',
    entity: type<TaskInstance>(),
    selectId: (entity) => entity.id,
  });

const _questionEntityConfig = () =>
  entityConfig({
    collection: '_questions',
    entity: type<TaskInstance>(),
    selectId: (entity) => entity.id,
  });

const _questionEntities = _questionEntityConfig();
const _sectionEntities = _sectionEntityConfig();
const _documentEntities = _documentEntityConfig();

function _determineEntityConfig(taskListType: TaskListType) {
  switch (taskListType) {
    case 'document':
      return _documentEntities;
    case 'section':
      return _sectionEntities;
    case 'question':
      return _questionEntities;
  }
}

export const withTaskEntities = (_injector: Injector) => {
  return signalStoreFeature(
    { state: type<TaskListState>() },
    withEntities(taskEntity()),
    withEntities(_documentEntities),
    withEntities(_sectionEntities),
    withEntities(_questionEntities),
    withReducer(
      on(
        TASK_EVENTS.patchTaskList,
        ({ payload }) =>
          (state) =>
            setAllEntities([...payload.taskList], taskEntity())(state),
      ),
      on(TASK_EVENTS.taskListLoaded, ({ payload }) => [
        setAllEntities([...payload.taskList], taskEntity()),
        {
          loading: false,
          taskListError: null,
          taskListLoading: false,
          tasksAlreadyLoaded: true,
        },
      ]),
      on(
        TASK_EVENTS.patchSectionTaskList,
        ({ payload }) =>
          (state) =>
            setAllEntities([...payload.taskList], _sectionEntities)(state),
      ),
      on(
        TASK_EVENTS.patchQuestionTaskList,
        ({ payload }) =>
          (state) =>
            setAllEntities([...payload.taskList], _questionEntities)(state),
      ),
      on(
        TASK_EVENTS.updateTaskInList,
        ({ payload }) =>
          (state) =>
            updateEntity(
              {
                changes: (task) => {
                  if (payload.hasError) {
                    return { ...task, is_completed: payload.task.is_completed };
                  }
                  return { ...payload.task };
                },
                id: payload.taskInstanceId,
              },
              _determineEntityConfig(payload.taskListType),
            )(state),
      ),
      on(
        TASK_EVENTS.bulkUpdateTaskStatusByIds,
        ({ payload }) =>
          (state) =>
            updateEntities(
              {
                changes: (task: TaskInstance) => ({
                  ...task,
                  is_completed: payload.isCompleted,
                }),
                ids: [...payload.taskIds],
              },
              _determineEntityConfig(payload.taskListType),
            )(state),
      ),
    ),
  );
};
